import { CONSTANTS } from '@js/global'
import { Draggable, setRandomPosition, SvgToggleButton, zIndexManager } from '@js/utils'
import { Position } from '@js/types'
import css from '@css/sass/styles.module.scss'
import { AssetManager } from '@js/loaders'

export interface BaseBoxOptions {
  id: string
  title: string
  width?: number          // in px
  widthMin?: number
  heightMin?: number
  heightOpen?: number | string // 'auto'
  bgAssetKey?: string     // key in AssetManager for background
  bgFolder?: string       // 'motifs' | 'captcha' | ...
  bgColor?: string
  bgClass?: string[] // background class
  extraClasses?: string[]
  initialState?: 'open' | 'close'
  draggableOptions?: Partial<import('@js/utils').DraggableOptions>
  resizable?: boolean
  zIndexGroup?: string
  marqueeSpeed?: number
}

export abstract class BaseBox {
  protected box: HTMLDivElement | null = null
  protected titleContainer: HTMLDivElement | null = null
  protected bodyContainer: HTMLDivElement | null = null
  protected draggable: Draggable | null = null
  protected storedPositionX: string | null = null
  protected storedPositionY: string | null = null
  protected isOpen: boolean = true

  protected readonly options: BaseBoxOptions
  
  protected btnClose: SvgToggleButton | null = null;
  protected bgLayer: HTMLDivElement | undefined
  protected closeIconDefault: string | undefined
  protected closeIconHover: string | undefined
  protected closeIconActive: string | undefined
  protected openIconDefault: string | undefined
  protected openIconHover: string | undefined
  protected openIconActive: string | undefined

  constructor(options: BaseBoxOptions) {
    this.options = {
      width: CONSTANTS.css.boxWidth,
      widthMin: CONSTANTS.css.boxWidth,
      heightMin: CONSTANTS.css.boxHeightMin,
      heightOpen: CONSTANTS.css.boxHeightMin * 2.5, // default – override in subclasses
      initialState: 'open',
      bgColor: 'red',
      draggableOptions: {},
      resizable: false,
      zIndexGroup: 'boxes',   // ← all boxes share the same stacking group by default
      marqueeSpeed: 50,        // ← default
      ...options,
    }
    // Set defaults (only once, here in base constructor)
    const assets = AssetManager.getInstance();
    this.closeIconDefault = assets.get('icons', 'croix.svg');
    this.closeIconHover   = assets.get('icons', 'croix_3.svg');
    this.closeIconActive  = assets.get('icons', 'croix_3.svg'); // or croix_2 if different
    this.openIconDefault  = assets.get('icons', 'plus.svg');
    this.openIconHover    = assets.get('icons', 'plus_3.svg');
    this.openIconActive   = assets.get('icons', 'plus_3.svg');
  }


  /* -----------------------------------------------------------------
  | E D I T A B L E   M E T H O D S 
  |-----------------------------------------------------------------*/

  // Override in subclasses
  protected abstract createContent(): void 

  // Hook for subclasses to show/hide buttons, pause audio, etc.
  protected onOpenChanged(isOpen: boolean): void {
    // override in subclass if needed
    if (this.bodyContainer) {
      isOpen === true ? this.bodyContainer.style.display = 'block' : this.bodyContainer.style.display = 'none'
    }
    // Extra safety: force height reset when closing
    if (!isOpen && this.box) {
      this.box.style.height = `${this.options.heightMin}px !important`;
      this.box.style.minHeight = '0';
      this.box.style.overflow = 'hidden'; // prevent any content bleed
    }
  }

  /* -----------------------------------------------------------------
  | G E N E R I C  -  Don't touch 
  |-----------------------------------------------------------------*/

  async setup(): Promise<void> {
    this.createMainContainer()
    this.createTitleContainer()
    this.createBodyContainer()
    this.createContent()           // ← subclass fills body + buttons etc.
    this.setupCloseToggleButton()
    this.setupDraggable()
    this.setupRandomPosition()

    this.options.initialState === 'close' ? this.setClose() : this.setOpen()

    document.body.appendChild(this.box!)
    console.debug(`[${this.constructor.name}] Setup complete`)
  }

  protected createMainContainer(): void {
    this.box = document.createElement('div');

    // Set ID (fallback to options.id if css lookup fails)
    this.box.id = css[this.options.id as keyof typeof css] || this.options.id;

    // Add extra classes safely
    if (this.options.extraClasses?.length) {
      this.box.classList.add(...this.options.extraClasses);
    }

    // Background layer (faded independently)
    this.bgLayer = document.createElement('div');
    this.bgLayer.className = 'box-bg-layer';
    if (this.options.bgClass?.length) this.bgLayer.classList.add(...this.options.bgClass);
    this.bgLayer.style.cssText = `
      position: absolute;
      inset: 0;
      z-index: -1;
      `;
    this.box.appendChild(this.bgLayer);


    // ─── Background logic ────────────────────────────────────────────────
    const bgUrl = this.getBackgroundUrl();

    if (bgUrl) {
      // Background image takes priority when available
      this.bgLayer.style.backgroundImage = `url(${bgUrl})`;

    } else if (this.options.bgColor) {
      // Fallback to solid color
      this.bgLayer.style.backgroundColor = this.options.bgColor;
    }

    // ─── Size ─────────────────────────────────────────────────────────────
    if (this.options.widthMin !== undefined) {
      this.box.style.width = `${this.options.widthMin}px`;
    }

    // Start minimized
    this.box.style.height = `${this.options.heightMin}px`;

    // ─── zIndex ───────────────────────────────────────────────────────────
    zIndexManager.register(this.options.id, this.box!)

  }

  protected createTitleContainer(): void {
    if (!this.box) return

    this.titleContainer = document.createElement('div')
    this.titleContainer.className = css.title
    this.box.appendChild(this.titleContainer)

    const titleEl = document.createElement('h2')
    titleEl.innerHTML = this.options.title
    this.titleContainer.appendChild(titleEl)
  }

  protected createBodyContainer(): void {
    this.bodyContainer = document.createElement('div')
    this.bodyContainer.className = css.body
    this.box?.appendChild(this.bodyContainer)
  }

  protected setupDraggable(): void {
    if (!this.box) return
    this.draggable = new Draggable(this.box, {
      containment: 'window',
      axis: 'both',
      cancelClass: css.svg_btn,        // your SVG buttons class
      ...this.options.draggableOptions,
      onStart: (element: HTMLElement, position: Position) => {
        zIndexManager.bringToFront(this.options.id)
        this.options.draggableOptions?.onStart?.(element, position)
      },
    })
    // also bring to front on click
    this.box.addEventListener('mouseenter', () => {
      zIndexManager.bringToFront(this.options.id)         // ← on click
    })
  }

  protected setupRandomPosition(): void {
    if (!this.box) return
    setRandomPosition(this.box)
    this.storedPositionX = this.box.style.left
    this.storedPositionY = this.box.style.top   // or .bottom – your choice
  }

  private setResizable(enabled: boolean): void {
    if (!this.box) return

    if (enabled && this.options.resizable) {
      this.box.style.resize = 'both'
    } else {
      this.box.style.resize = 'none'
      // flush any size the browser cached from a previous resize
      this.box.style.maxWidth = `${this.options.widthMin}px`
      this.box.style.maxHeight = `${this.options.heightMin}px`
      requestAnimationFrame(() => {
        if (!this.box) return
        this.box.style.maxWidth = ''
        this.box.style.maxHeight = ''
      })
    }
  }

  setOpen(): void {
    if (!this.box) return

    this.isOpen = true;
    this.btnClose?.setToggled(false); // show CLOSE icon
  
    if (this.options.resizable) {
      this.setResizable(true)
    }

    this.box.style.height = this.options.heightOpen === 'auto' ? 'fit-content' : `${this.options.heightOpen}px`
    this.box.style.width = `${this.options.width}px`
    this.box.style.left = this.storedPositionX || '20px'
    this.box.style.top  = this.storedPositionY || '20px'

    if (this.draggable) {
        this.draggable.updateOptions({ axis: 'both' });   // ← restrict to horizontal
      }

    // Apply height
    if (this.options.heightOpen === 'auto') {
      this.box.style.height = 'fit-content'
      this.box.style.minHeight = 'min(80vh, 510px)'   // never too small, never full screen
    } else {
      this.box.style.height = `${this.options.heightOpen}px`
    }
    // Optional: force layout recalculation
    requestAnimationFrame(() => {
        void this.box?.offsetHeight
    })

    this.onOpenChanged(true)
  }

  setClose(): void {
    if (!this.box) return
    
    this.isOpen = false
    this.btnClose?.setToggled(true); // show CLOSE icon

    if (this.options.resizable) {
      this.setResizable(false)
    }

    this.storedPositionX = this.box.style.left
    this.storedPositionY = this.box.style.top
    this.box.style.position = 'fixed'
    this.box.style.bottom = '0px'
    this.box.style.top  = 'auto'
    this.box.style.height = `${this.options.heightMin}px`
    this.box.style.width = `${this.options.widthMin}px`

    if (this.draggable) {
        this.draggable.updateOptions({ axis: 'x' });   // ← restrict to horizontal
      }

    this.onOpenChanged(false)
  }

  protected setupCloseToggleButton(): void {
    if (!this.titleContainer) return;

    // Check icons exist
    if (
      !this.closeIconDefault ||
      !this.closeIconHover ||
      !this.openIconDefault ||
      !this.openIconHover
    ) {
      console.warn('[BaseBox] Missing one or more close/open icons');
      return;
    }

    this.btnClose = new SvgToggleButton({
      parent: this.titleContainer,
      id: `${this.options.id}_btn_close`,
      images: {
        default: this.closeIconDefault,
        hover: this.closeIconHover,
        active: this.closeIconActive,
      },
      imagesToggled: {
        default: this.openIconDefault,
        hover: this.openIconHover,
        active: this.openIconActive,
      },
      floatStyle: 'right',
      onToggle: (isToggled) => {
        // User clicked → toggle box state
        if (isToggled) {
          this.setClose();
        } else {
          this.setOpen();
        }
      },
    });

    // Optional: initial state
    this.btnClose.setToggled(!this.isOpen); // closed → show open icon
  }

  protected disableDragging(): void {
    if (this.draggable) {
      this.draggable.disable()
      // Optional: visual feedback
      if (this.box) {
        this.box.style.cursor = 'default'
        this.box.classList.remove(css.makeDraggable) // optional
      }
    }
  }

  protected enableDragging(): void {
    if (this.draggable) {
      this.draggable.enable()
      if (this.box) {
        this.box.style.cursor = 'grab'
        this.box.classList.add(css.makeDraggable) // optional
      }
    }
  }

  destroy(): void {
    this.draggable?.destroy()
    this.draggable = null

    this.box?.parentElement?.removeChild(this.box)
    this.box = null

    this.btnClose?.destroy()
    this.btnClose = null
  }

  /** -------------------------------------------------------------------------
  |   HELPERS
  | --------------------------------------------------------------------------*/
  
  private getBackgroundUrl(): string | undefined {
    /* Helper: Safely retrieve background URL from AssetManager */
    if (!this.options.bgAssetKey || !this.options.bgFolder) {
      return undefined;
    }

    const assets = AssetManager.getInstance();
    return assets.get(this.options.bgFolder, this.options.bgAssetKey);
  }


}
